import { Link, useNavigate } from "@tanstack/react-router";
import { Images, ShieldOff } from "lucide-react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { useProjects } from "@/hooks/use-projects";
import { hasPmAccess } from "@/lib/access";
import { ApiError } from "@/lib/api-client";
import { useSession } from "@/lib/auth-client";
import { progressLabel } from "@/lib/checklist";
import { formatDate } from "@/lib/format";
import { photoFileUrl } from "@/lib/photo-files";
import type { ProjectRow } from "@/schemas/photos";

const PROJECT_START = { tab: "albums", order: "desc" } as const;

const HEADERS = [
  { label: "Project", sortKey: "name" },
  { label: "SP number", sortKey: "spNumber", className: "hidden sm:table-cell" },
  { label: "Photos", sortKey: "photoCount", className: "text-right" },
  {
    label: "Albums with photos",
    sortKey: "albumCount",
    className: "hidden text-right sm:table-cell",
  },
  { label: "Last upload", sortKey: "lastUpload", className: "hidden text-right md:table-cell" },
  { label: "MC checklist", className: "hidden lg:table-cell" },
];

function ProjectTableRow({ project }: { project: ProjectRow }) {
  const navigate = useNavigate();
  const open = () =>
    void navigate({
      to: "/dashboard/projects/$projectId",
      params: { projectId: project.id },
      search: PROJECT_START,
    });

  return (
    <tr
      data-testid="project-row"
      onClick={open}
      className="cursor-pointer border-b transition-colors last:border-b-0 hover:bg-muted/50"
    >
      <td className="px-4 py-3">
        <span className="flex items-center gap-3">
          <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
            {project.coverPhotoId ? (
              <img
                src={photoFileUrl(project.coverPhotoId, "thumb")}
                alt=""
                decoding="async"
                className="size-full object-cover"
              />
            ) : (
              <Images className="size-5 text-muted-foreground/50" />
            )}
          </span>
          <span className="min-w-0">
            <Link
              to="/dashboard/projects/$projectId"
              params={{ projectId: project.id }}
              search={PROJECT_START}
              onClick={(event) => event.stopPropagation()}
              className="font-medium hover:underline"
            >
              {project.name}
            </Link>
            {project.spNumber && (
              <span className="block text-xs text-muted-foreground sm:hidden">
                {project.spNumber}
              </span>
            )}
          </span>
        </span>
      </td>
      <td className="hidden px-4 py-3 sm:table-cell">{project.spNumber ?? "—"}</td>
      <td className="px-4 py-3 text-right tabular-nums">
        {project.photoCount.toLocaleString("en-US")}
      </td>
      <td className="hidden px-4 py-3 text-right tabular-nums sm:table-cell">
        {project.albumCount}
      </td>
      <td className="hidden px-4 py-3 text-right tabular-nums md:table-cell">
        {formatDate(project.lastUpload)}
      </td>
      <td data-testid="checklist-progress" className="hidden px-4 py-3 text-sm lg:table-cell">
        {project.checklist ? progressLabel(project.checklist) : "—"}
      </td>
    </tr>
  );
}

export function DashboardPage() {
  const { data: session } = useSession();
  const grid = useDataGrid({ defaultSort: "name", defaultSortDir: "asc" });
  const { data, isPending, isError, error, refetch, isFetching } = useProjects(
    grid.query,
    grid.pageRequest,
  );
  const rows = data?.data ?? [];
  const noAccess = error instanceof ApiError && error.status === 403;
  const searching = grid.query.search !== "";
  const nothingShared = !searching && session && !hasPmAccess(session);

  return (
    <>
      <PageHeader title="Projects" className="mb-6" />
      {noAccess ? (
        <EmptyState
          icon={ShieldOff}
          title="This account has no access to photos"
          description="Ask a Madison project manager to finish setting it up."
          className="rounded-lg border border-dashed border-border py-20"
        />
      ) : isError ? (
        <ErrorState message="Could not load the projects" onRetry={() => refetch()} />
      ) : (
        <div className="space-y-4">
          <SearchInput
            value={grid.searchInput}
            onValueChange={grid.setSearch}
            placeholder="Search by name or SP number"
            className="w-full md:max-w-sm"
          />
          <DataTable
            headers={HEADERS}
            loading={isPending}
            sort={grid.sort}
            sortDir={grid.sortDir}
            onSort={grid.toggleSort}
            className="w-full"
            empty={
              <EmptyState
                icon={Images}
                title={
                  nothingShared
                    ? "Nothing has been shared with your firm yet"
                    : searching
                      ? "No projects match this search"
                      : "No projects to show yet"
                }
                description={
                  nothingShared
                    ? "Projects appear here once a Madison project manager shares them."
                    : undefined
                }
              />
            }
          >
            {rows.map((project) => (
              <ProjectTableRow key={project.id} project={project} />
            ))}
          </DataTable>
          {data && data.total > DEFAULT_LIMIT && (
            <CursorPagination
              total={data.total}
              page={grid.page}
              limit={DEFAULT_LIMIT}
              hasNext={data.hasNext}
              hasPrev={data.hasPrev}
              onNext={() => grid.goNext(data.nextCursor)}
              onPrev={() => grid.goPrev(data.prevCursor)}
              loading={isFetching}
              itemLabel="projects"
            />
          )}
        </div>
      )}
    </>
  );
}
